import { Box, Typography } from '@mui/material';
import { type ReactNode } from 'react';

import { ArtifactStatusChip } from '../ArtifactStatusChip/ArtifactStatusChip';
import { PageHeaderBreadcrumb } from '../PageHeader/types';
import {
  ProjectDetailHeader,
  type ProjectDetailHeaderMetaItem,
} from '../ProjectDetailHeader/ProjectDetailHeader';

export interface ArtifactDetailHeaderProps {
  /** Stable id prefix for testids (shared with the breadcrumb header). */
  id: string;
  /** Breadcrumb trail; omit for hosts without a breadcrumb. */
  breadcrumbs?: PageHeaderBreadcrumb[];
  /** Artifact name rendered as the page title. */
  title?: string;
  /** While true the title renders as a skeleton and the chip is withheld. */
  isLoading?: boolean;
  /** Status (any casing) for the inline chip; omit to hide it. */
  status?: string;
  /** Testid for the title element. Defaults to `${id}-title`. */
  titleTestId?: string;
  /** Testid for the status chip. Defaults to `${id}-status`. */
  statusTestId?: string;
  /** Right-aligned actions on the title row (buttons, menus). */
  rightContent?: ReactNode;
  /** Single line between the title and the meta row — see
   *  {@link ProjectDetailHeader.subtitle}. */
  subtitle?: ReactNode;
  /** Condensed meta row beneath the title. Omit/empty to hide it. */
  metaItems?: ProjectDetailHeaderMetaItem[];
}

/**
 * Detail-page header for project artifacts: breadcrumb, title with an
 * inline status chip, right-aligned actions, and the condensed meta row.
 * Composes {@link ProjectDetailHeader}; meta items stay domain-supplied.
 */
export const ArtifactDetailHeader = ({
  id,
  breadcrumbs,
  title,
  isLoading = false,
  status,
  titleTestId,
  statusTestId,
  rightContent,
  subtitle,
  metaItems,
}: ArtifactDetailHeaderProps) => {
  const titleSlot = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <Typography
        variant="h5"
        component="h1"
        data-testid={titleTestId ?? `${id}-title`}
        sx={{ fontWeight: 400, minWidth: 0, overflowWrap: 'anywhere' }}
      >
        {title}
      </Typography>
      {status && (
        <ArtifactStatusChip status={status} data-testid={statusTestId ?? `${id}-status`} />
      )}
    </Box>
  );

  return (
    <ProjectDetailHeader
      id={id}
      breadcrumbs={breadcrumbs}
      title={title}
      isTitleLoading={isLoading}
      titleSlot={isLoading ? undefined : titleSlot}
      rightContent={rightContent}
      subtitle={subtitle}
      metaItems={metaItems}
    />
  );
};
